import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import { toast } from "sonner";
import { Banknote, Plus, Trash2, Pencil, FileText, Download, Calendar } from "lucide-react";
import RecordRentPaymentDialog from "../components/RecordRentPaymentDialog";
import { methodLabel } from "../components/VendorPaymentsDialog";
import { fmtUSDate as toUSDate } from "../lib/date";

const currency = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Payments() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  // When non-null, the Record/Edit dialog opens in EDIT mode with this row's
  // values pre-populated. We funnel both "new" and "edit" through the same
  // dialog so the form layout and validation stay in one place.
  const [editingPayment, setEditingPayment] = useState(null);

  // Date-range report state
  const thisYear = new Date().getFullYear();
  const [rptStart, setRptStart] = useState(`${thisYear}-01-01`);
  const [rptEnd, setRptEnd] = useState(`${thisYear}-12-31`);
  const [rptSort, setRptSort] = useState("date_desc");
  const [rpt, setRpt] = useState(null);
  const [rptBusy, setRptBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/manual?limit=200");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payments");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const generateReport = async () => {
    setRptBusy(true);
    try {
      const { data } = await api.get("/payments/manual/report", {
        params: { start_date: rptStart, end_date: rptEnd, sort_by: rptSort },
      });
      setRpt(data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to load report";
      toast.error(typeof msg === "string" ? msg : "Failed to load report");
      setRpt(null);
    } finally {
      setRptBusy(false);
    }
  };

  const downloadReportPdf = async () => {
    try {
      await authenticatedDownload(
        "/api/payments/manual/report/pdf",
        `rent-payments_${rptStart}_to_${rptEnd}.pdf`,
        { start_date: rptStart, end_date: rptEnd, sort_by: rptSort },
      );
    } catch {
      toast.error("PDF download failed");
    }
  };

  const downloadReportCsv = async () => {
    try {
      await authenticatedDownload(
        "/api/payments/manual/report/csv",
        `rent-payments_${rptStart}_to_${rptEnd}.csv`,
        { start_date: rptStart, end_date: rptEnd, sort_by: rptSort },
      );
    } catch {
      toast.error("CSV download failed");
    }
  };

  const today = () => new Date().toISOString().slice(0, 10);
  const isoBack = (days) =>
    new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

  const remove = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await api.delete(`/payments/manual/${id}`);
      toast.success("Payment deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const total = (items || []).reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="overline text-slate-500">Money in</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
              Payments
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Record rent payments that came in by check, cash, ACH, Zelle, CashApp, or other off-platform methods.
              Online (Stripe) payments appear automatically and are managed in the Tenant ledger.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="payments-record-cta"
            className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-sm"
          >
            <Plus className="w-4 h-4" /> Record payment
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Stat label="Total recorded" value={`$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} testid="payments-stat-total" bgClass="bg-green-50 border-green-200" />
          <Stat label="# of payments" value={(items?.length || 0).toString()} testid="payments-stat-count" bgClass="bg-amber-50 border-amber-200" />
          <Stat
            label="Last payment"
            value={items && items[0] ? toUSDate(items[0].paid_at) : "—"}
            testid="payments-stat-last"
            bgClass="bg-blue-50 border-blue-200"
          />
        </div>

        {/* Date-range report panel */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6" data-testid="payments-report-panel">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-700" />
              <h3 className="text-sm font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
                Payments Report
              </h3>
              <span className="text-xs text-slate-500">— filter by date range, preview and download as PDF.</span>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Start date</label>
              <input
                type="date"
                className="input-clean mt-2 w-full"
                data-testid="payments-report-start"
                value={rptStart}
                onChange={(e) => setRptStart(e.target.value)}
              />
            </div>
            <div>
              <label className="overline text-slate-600 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> End date</label>
              <input
                type="date"
                className="input-clean mt-2 w-full"
                data-testid="payments-report-end"
                value={rptEnd}
                onChange={(e) => setRptEnd(e.target.value)}
              />
            </div>
            <div>
              <label className="overline text-slate-600">Sort by</label>
              <select
                className="input-clean mt-2 w-full"
                data-testid="payments-report-sort"
                value={rptSort}
                onChange={(e) => setRptSort(e.target.value)}
              >
                <option value="date_desc">Date — newest first</option>
                <option value="date_asc">Date — oldest first</option>
                <option value="property">Property (A-Z)</option>
                <option value="amount">Amount (largest first)</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={generateReport}
                disabled={rptBusy}
                data-testid="payments-report-generate"
                className="btn-primary-dark w-full disabled:opacity-60"
              >
                {rptBusy ? "Generating…" : "Generate report"}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-3">
            <span className="text-xs text-slate-500">Quick presets:</span>
            {[
              ["This month", `${thisYear}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`, today()],
              ["Last 30d", isoBack(30), today()],
              ["YTD", `${thisYear}-01-01`, today()],
            ].map(([label, s, e]) => (
              <button
                key={label}
                onClick={() => { setRptStart(s); setRptEnd(e); }}
                data-testid={`payments-report-preset-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-1.5 rounded-sm transition-colors border border-slate-200"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-100">
            <button
              onClick={downloadReportCsv}
              disabled={!rpt}
              data-testid="payments-report-download-csv"
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-900 text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
              title="Download a CSV of these payments for Excel / QuickBooks"
            >
              <Download className="w-4 h-4" /> Download CSV
            </button>
            <button
              onClick={downloadReportPdf}
              disabled={!rpt}
              data-testid="payments-report-download-pdf"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-black text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
              title="Download a printable Rent Payments report PDF for this date range"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>

          {rpt && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="payments-report-kpis">
                <ReportKpi label="# of payments" value={rpt.totals.count} tone="blue" />
                <ReportKpi label="Total collected" value={currency(rpt.totals.total)} tone="green" />
                <ReportKpi label="Average payment" value={currency(rpt.totals.average)} tone="slate" />
                <ReportKpi label="Methods used" value={rpt.by_method.length} tone="amber" />
              </div>

              {rpt.by_method.length > 0 && (
                <div className="mt-4 border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm" data-testid="payments-report-method-table">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2.5">Method</th>
                        <th className="text-right px-4 py-2.5"># of Payments</th>
                        <th className="text-right px-4 py-2.5">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rpt.by_method.map((m) => (
                        <tr key={m.method} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="px-4 py-2 text-slate-800 font-semibold">{methodLabel(m.method)}</td>
                          <td className="px-4 py-2 text-right font-mono">{m.count}</td>
                          <td className="px-4 py-2 text-right font-mono">{currency(m.total)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-900 text-white">
                        <td className="px-4 py-2.5 font-bold">Totals</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold">{rpt.totals.count}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold">{currency(rpt.totals.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4 border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-report-detail-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2.5">Date</th>
                      <th className="text-left px-4 py-2.5">Property</th>
                      <th className="text-left px-4 py-2.5">Tenant</th>
                      <th className="text-left px-4 py-2.5">Method</th>
                      <th className="text-left px-4 py-2.5">Check #</th>
                      <th className="text-left px-4 py-2.5">Notes</th>
                      <th className="text-right px-4 py-2.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rpt.rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-10 text-slate-400" data-testid="payments-report-empty">
                          No payments recorded in this window.
                        </td>
                      </tr>
                    ) : (
                      rpt.rows.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="px-4 py-2 font-mono text-xs text-slate-600">{toUSDate(r.paid_at)}</td>
                          <td className="px-4 py-2 font-semibold text-slate-900">
                            {r.property_name}
                            {r.unit_label ? <span className="text-slate-400 font-normal"> · {r.unit_label}</span> : null}
                          </td>
                          <td className="px-4 py-2 text-slate-700">{r.tenant_name}</td>
                          <td className="px-4 py-2 text-slate-700">{methodLabel(r.payment_method)}</td>
                          <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.check_number || "—"}</td>
                          <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-[260px]">{r.notes || r.reference || "—"}</td>
                          <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">{currency(r.amount)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {rpt.rows.length > 0 && (
                    <tfoot>
                      <tr className="bg-slate-900 text-white">
                        <td colSpan={6} className="px-4 py-2.5 font-bold">Totals</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold">{currency(rpt.totals.total)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {items === null ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="payments-empty">
              <Banknote className="w-8 h-8 mx-auto text-slate-300 mb-3" />
              <div className="font-semibold text-slate-700">No manual payments recorded yet.</div>
              <div className="text-sm mt-1">Click "Record payment" to log a rent payment that came in by check, cash, ACH, etc.</div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Lease</th>
                  <th className="text-left px-4 py-3">Method</th>
                  <th className="text-left px-4 py-3">Check #</th>
                  <th className="text-left px-4 py-3">Notes</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3"> </th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`payments-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{toUSDate(p.paid_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.lease_id?.slice(0, 8)}…</td>
                    <td className="px-4 py-3 text-slate-700">{methodLabel(p.payment_method)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.check_number || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[260px]">{p.notes || p.reference || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">
                      ${(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => { setEditingPayment(p); setOpen(true); }}
                          className="text-slate-400 hover:text-blue-600 p-1"
                          title="Edit payment"
                          data-testid={`payments-edit-${p.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => remove(p.id)}
                          className="text-slate-400 hover:text-red-600 p-1"
                          title="Delete"
                          data-testid={`payments-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <RecordRentPaymentDialog
        open={open}
        editing={editingPayment}
        onClose={() => { setOpen(false); setEditingPayment(null); }}
        onRecorded={async () => {
          setOpen(false);
          setEditingPayment(null);
          await load();
        }}
      />
    </DashboardShell>
  );
}

function Stat({ label, value, testid, bgClass = "bg-white border-slate-200" }) {
  return (
    <div className={`${bgClass} border rounded-sm p-4`} data-testid={testid}>
      <div className="overline text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold font-mono tracking-tight text-slate-900">{value}</div>
    </div>
  );
}

function ReportKpi({ label, value, tone = "slate" }) {
  const tones = {
    blue:  { box: "bg-blue-50 border-blue-200",       label: "text-blue-700",    value: "text-blue-700" },
    green: { box: "bg-emerald-50 border-emerald-200", label: "text-emerald-700", value: "text-emerald-700" },
    amber: { box: "bg-amber-50 border-amber-200",     label: "text-amber-800",   value: "text-amber-900" },
    slate: { box: "bg-slate-50 border-slate-200",     label: "text-slate-600",   value: "text-slate-900" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`border rounded-sm p-4 ${t.box}`}>
      <div className={`overline ${t.label}`}>{label}</div>
      <div className={`mt-1 text-2xl font-bold font-mono tracking-tight ${t.value}`}>{value}</div>
    </div>
  );
}
